import { Outlet, Link, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { Menu, X, ShoppingCart, User, Globe } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

export default function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'My Domains', href: '/domains' },
    { name: 'Orders', href: '/orders' },
  ]

  return (
    <div className="min-h-screen bg-louis-black">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-louis-black/80 backdrop-blur-lg border-b border-white/10">
        <nav className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <Globe className="w-8 h-8 text-louis-gold" />
              <span className="text-xl font-bold">
                <span className="text-louis-gold">Louis</span>
                <span className="text-white"> Domains</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-sm font-medium transition-colors hover:text-louis-gold ${
                    location.pathname === item.href ? 'text-louis-gold' : 'text-white/70'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Right side actions */}
            <div className="flex items-center gap-4">
              <Link
                to="/cart"
                className="relative p-2 text-white/70 hover:text-louis-gold transition-colors"
              >
                <ShoppingCart className="w-5 h-5" />
                {/* Cart badge - will be dynamic */}
              </Link>

              <Link
                to="/login"
                className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg border border-white/20 text-white/70 hover:border-louis-gold hover:text-louis-gold transition-all"
              >
                <User className="w-4 h-4" />
                <span className="text-sm">Login</span>
              </Link>

              {/* Mobile menu button */}
              <button
                className="md:hidden p-2 text-white"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-louis-black border-t border-white/10"
            >
              <div className="container mx-auto px-4 py-4 space-y-4">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block py-2 text-sm font-medium ${
                      location.pathname === item.href ? 'text-louis-gold' : 'text-white/70'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 py-2 text-sm font-medium text-white/70"
                >
                  <User className="w-4 h-4" />
                  Login
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main content */}
      <main className="pt-20">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-louis-black border-t border-white/10 py-12 mt-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Logo and description */}
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center gap-2 mb-4">
                <Globe className="w-8 h-8 text-louis-gold" />
                <span className="text-xl font-bold">
                  <span className="text-louis-gold">Louis</span>
                  <span className="text-white"> Domains</span>
                </span>
              </Link>
              <p className="text-white/60 text-sm max-w-md">
                Qatar's trusted domain registration service. Register your .qa domain today
                and establish your online presence in Qatar.
              </p>
            </div>

            {/* Quick links */}
            <div>
              <h3 className="text-white font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-white/60 text-sm hover:text-louis-gold transition-colors">
                    Search Domains
                  </Link>
                </li>
                <li>
                  <Link to="/domains" className="text-white/60 text-sm hover:text-louis-gold transition-colors">
                    My Domains
                  </Link>
                </li>
                <li>
                  <Link to="/orders" className="text-white/60 text-sm hover:text-louis-gold transition-colors">
                    Order History
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-white font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-white/60 text-sm">
                <li>info@louis-innovations.com</li>
                <li>+974-70259259</li>
                <li>Doha, Qatar</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm">
              {new Date().getFullYear()} Louis Innovations. All rights reserved.
            </p>
            <div className="flex gap-4">
              <a href="https://louis-innovations.com" target="_blank" rel="noopener noreferrer" className="text-white/40 text-sm hover:text-louis-gold transition-colors">
                Main Website
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
